import type { Travel, TravelLeg } from "@housing/shared";

/**
 * Turning the stored times into the handful of rows a card actually shows.
 *
 * No zod here on purpose: `travel` arrives inside the `/api/listings` payload and
 * was already parsed against the shared schema at that boundary
 * (`listings/listing.api.ts`). This module derives from validated data.
 */

/**
 * A scooter shares the car's lanes, so its time tracks driving, not transit.
 *
 * It runs about 15% quicker across town: it filters through stopped traffic
 * (legal in California) and parks in seconds instead of circling for a space.
 * But that advantage is a fixed city-traffic dividend, not a percentage that
 * keeps compounding — on a long freeway run there is nothing to filter past and
 * no garage to skip, and many small scooters aren't freeway-legal at all. So the
 * saving is capped in absolute terms too. Without the cap, a 90-minute drive
 * would render as a confident, fictional 76-minute ride.
 *
 * A 20-minute drive → 17 min (the share binds). A 90-minute drive → 82 min (the
 * cap binds).
 */
const SCOOTER_SHARE_OF_DRIVE = 0.85;
const SCOOTER_MAX_SAVING_MIN = 8;

/**
 * Past this, a scooter stops being an answer. The ingest caps walking at an hour
 * for the same reason — a stored time long enough to be useless still gets read
 * as a suggestion — and driving is stored out to three hours, so without a
 * ceiling here a card would cheerfully offer "~2 hr 52 min" on a moped.
 */
const SCOOTER_MAX_MIN = 90;

/**
 * `walkMin` is a ceiling, not a hint.
 *
 * The estimate comes off the driving time, which carries costs a scooter mostly
 * doesn't and a walk doesn't at all — one-way systems, the block you circle, the
 * minute spent parking. Over a few hundred metres those dominate, and the card
 * ends up claiming a scooter is slower than walking to the same door. It cannot
 * be: the worst a scooter can do is be pushed. So walking bounds it, which also
 * settles the case that reads worst — a listing a minute away showing "~0 min"
 * to walk and something larger to ride.
 */
function scooterMinutes(driveMin: number | null, walkMin: number | null): number | null {
  if (driveMin == null) return null;
  const est = Math.max(driveMin * SCOOTER_SHARE_OF_DRIVE, driveMin - SCOOTER_MAX_SAVING_MIN);
  const mins = Math.max(1, Math.round(est));
  if (mins > SCOOTER_MAX_MIN) return null;
  return walkMin == null ? mins : Math.min(mins, walkMin);
}

/**
 * How much faster the multi-leg journey has to be before it earns a row.
 *
 * The point of that line is "changing buses twice buys you something". If it
 * saves a minute over just walking, it buys nothing, and showing it turns a
 * two-line answer into a three-line comparison the reader has to perform.
 */
const COMBINED_MIN_EDGE_MIN = 3;

/**
 * Internal key, not the on-screen word. "transit" is what the column is; the
 * label is "Combined", because what earns the row is that the journey chains
 * several modes together. The key stays as-is because it is persisted in saved
 * filter state, where a renamed enum value would fail validation on load.
 */
export type TravelMode = "walk" | "scooter" | "transit";

interface Normalized {
  walk: number | null;
  scooter: number | null;
  combined: number | null;
  legs: TravelLeg[];
}

/**
 * Reconcile the stored columns into the three things a reader cares about.
 *
 * The one substantive move: a stored itinerary whose every leg is walking is not
 * a transit option at all — it is TravelTime answering "just walk" (180 of the
 * 1290 stored routes are exactly this). Rendering it as a transit journey, tram
 * icon and all, is the confusion this panel exists to remove. So it collapses
 * into the walk time instead, and only supplies that time when the walking
 * matrix hasn't already given us one.
 */
function normalize(travel: Travel): Normalized {
  const walkOnlyRoute = travel.legs.length > 0 && travel.legs.every((l) => l.mode === "walk");
  const walk = travel.walk ?? (walkOnlyRoute ? travel.transit : null);
  return {
    walk,
    scooter: scooterMinutes(travel.drive, walk),
    combined: walkOnlyRoute ? null : travel.transit,
    legs: walkOnlyRoute ? [] : travel.legs,
  };
}

export interface TravelRow {
  mode: TravelMode;
  mins: number;
  /** True for the single quickest row — the one worth emphasising. */
  fastest: boolean;
  /** Combined only: the stored itinerary. Empty when no breakdown was fetched. */
  legs: TravelLeg[];
  /** Combined only: minutes saved against the best single-mode option. */
  savesMin: number | null;
}

/**
 * The rows to render, in fixed walk → scooter → combined order.
 *
 * Order is positional rather than fastest-first so the same mode sits in the
 * same place on every card; the `fastest` flag carries the ranking instead.
 *
 * A mode with no stored time is simply absent — the ingest stores NULL when a
 * trip exceeds that mode's ceiling, which means "not an option this way", not
 * "unknown". The combined row additionally has to beat the alternatives to
 * appear at all, so a place you can walk to in 10 minutes doesn't also advertise
 * a 9-minute bus.
 */
export function travelRows(travel: Travel): TravelRow[] {
  const t = normalize(travel);

  const rows: { mode: TravelMode; mins: number }[] = [];
  if (t.walk != null) rows.push({ mode: "walk", mins: t.walk });
  if (t.scooter != null) rows.push({ mode: "scooter", mins: t.scooter });

  // Best single-mode time to beat. Null when the journey is all we know about.
  const best = rows.length > 0 ? Math.min(...rows.map((r) => r.mins)) : null;
  if (t.combined != null && (best === null || t.combined <= best - COMBINED_MIN_EDGE_MIN)) {
    rows.push({ mode: "transit", mins: t.combined });
  }

  if (rows.length === 0) return [];
  // First row achieving the minimum, not every row tied at it: two bolded lines
  // would defeat the point of marking one.
  const quickest = Math.min(...rows.map((r) => r.mins));
  const fastestIndex = rows.findIndex((r) => r.mins === quickest);

  return rows.map((r, i) => ({
    mode: r.mode,
    mins: r.mins,
    fastest: i === fastestIndex,
    legs: r.mode === "transit" ? t.legs : [],
    savesMin: r.mode === "transit" && best !== null ? best - r.mins : null,
  }));
}

/**
 * This unit's time by one mode, or by whichever is quickest ("any").
 *
 * Null means "no time that way" — past the ingest's ceiling, or never priced.
 * The filter treats null as failing a limit, because a time we don't have can't
 * be promised.
 *
 * "any" deliberately reads the *rendered* rows rather than the raw columns. A
 * filter that counted a suppressed journey would keep listings whose card then
 * shows nothing under the cap the reader just set — "Any ≤ 33 min" leaving a
 * card whose only line says ~34 min. Naming a specific mode still uses that
 * mode's own time, suppressed or not, since asking for "transit under 20" is a
 * question about transit, not about what the card chose to show.
 */
export function travelMinutesFor(travel: Travel, mode: TravelMode | "any"): number | null {
  if (mode === "any") {
    const rows = travelRows(travel);
    return rows.length > 0 ? Math.min(...rows.map((r) => r.mins)) : null;
  }
  const t = normalize(travel);
  if (mode === "walk") return t.walk;
  if (mode === "scooter") return t.scooter;
  return t.combined;
}
